import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "@/hooks/auth";
import { useVoiceSamplePlayer } from "@/hooks/audio";
import { brand, layout } from "@/constants";
import {
  ScreenLayout,
  TextButton,
  NotificationCard,
  Text,
  Title,
  VoiceListItem,
  ScrollView,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for M7 language-picker re-enablement
  Dropdown
} from "@/components";
import {
  getAvailableVoices,
  getDefaultVoice,
  getVoiceSamplePath,
  Voice
} from "@/utils/voices";
import {
  getAvailableLanguages,
  getDefaultLanguage,
  Language
} from "@/utils/languages";
import { updateUserSettings } from "@/store/users";

/**
 * Settings screen component that allows users to select their preferred agent voice and language
 * Shown during onboarding after consent and accessible from drawer for future changes
 */
export default function SettingsScreen() {
  const { user, refreshUserData } = useAuth();
  const router = useRouter();
  const { play, stop, isPlaying } = useVoiceSamplePlayer();

  const [voices] = useState<Voice[]>(getAvailableVoices());
  const [languages] = useState<Language[]>(getAvailableLanguages());
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(
    user?.settings?.voice?.selectedVoiceId || null
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    user?.settings?.language?.selectedLanguage || null
  );
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stop audio when component unmounts
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  // Update playing state when audio stops
  useEffect(() => {
    if (!isPlaying) {
      setPlayingVoiceId(null);
    }
  }, [isPlaying]);

  // Update state when user data changes (e.g., on reload or after refresh)
  useEffect(() => {
    if (user?.settings?.voice?.selectedVoiceId) {
      setSelectedVoiceId(user.settings.voice.selectedVoiceId);
    }
    if (user?.settings?.language?.selectedLanguage) {
      setSelectedLanguage(user.settings.language.selectedLanguage);
    }
  }, [user]);

  // Set default voice if no selection and not already set
  useEffect(() => {
    if (!selectedVoiceId && voices.length > 0) {
      setSelectedVoiceId(getDefaultVoice().id);
    }
  }, [selectedVoiceId, voices]);

  // Set default language if no selection and not already set
  useEffect(() => {
    if (!selectedLanguage && languages.length > 0) {
      setSelectedLanguage(getDefaultLanguage().id);
    }
  }, [selectedLanguage, languages]);

  const handleVoiceSelect = (voiceId: string) => {
    setSelectedVoiceId(voiceId);
    setError(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for M7 language-picker re-enablement
  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language);
    setError(null);
  };

  const handlePlaySample = async (voiceId: string) => {
    try {
      setError(null);

      // Toggle play/pause for the same voice
      if (playingVoiceId === voiceId && isPlaying) {
        await stop();
        return;
      }

      // Stop current audio if playing
      if (isPlaying) {
        await stop();
      }

      // Set playing voice ID and start playing
      setPlayingVoiceId(voiceId);
      await play(getVoiceSamplePath(voiceId));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to play voice sample"
      );
      setPlayingVoiceId(null);
    }
  };

  const handleContinue = async () => {
    if (!selectedLanguage) {
      setError("Please select a language to continue");
      return;
    }
    if (!selectedVoiceId) {
      setError("Please select a voice to continue");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await updateUserSettings(user!.uid, {
        voice: { selectedVoiceId },
        language: { selectedLanguage }
      });

      // Refresh user data to include the new settings
      await refreshUserData();

      // Navigate to chat — /settings is a terminal route, so AuthGate keeps
      // the user here even after the voice id is set. We explicitly route on
      // to /chat to complete onboarding.
      router.replace("/(drawer)/chat");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setIsLoading(false);
    }
  };

  const isFirstTimeSetup = !user?.settings?.voice?.selectedVoiceId;

  return (
    <ScreenLayout>
      <ScrollView>
        {/* Header */}
        <Title
          subtitle={`Customize your experience with ${brand.name}.${isFirstTimeSetup ? " You can change this anytime in settings." : ""}`}
        >
          Settings
        </Title>

        {/* Language Selection — hidden for now (English-only beta).
            Re-enable when multi-language support ships with a validated
            safety stack. The state, defaults, and save path are kept live
            so re-enablement is purely UI. */}
        {/* <View style={styles.section}>
          <Text
            variant="semiBold"
            size="large"
            color="primary"
            style={styles.sectionTitle}
          >
            Language
          </Text>
          <View style={styles.dropdownContainer}>
            <Dropdown
              options={languages.map((lang) => ({
                id: lang.id,
                label: lang.id
              }))}
              selectedId={selectedLanguage || ""}
              onSelect={handleLanguageSelect}
              placeholder="Select Language"
            />
          </View>
        </View> */}

        {/* Voice Selection List */}
        <View style={styles.section}>
          <Text
            variant="semiBold"
            size="large"
            color="primary"
            style={styles.sectionTitle}
          >
            Voice
          </Text>
          <View style={styles.voiceList}>
            {voices.map((voice) => (
              <VoiceListItem
                key={voice.id}
                voice={voice}
                isSelected={selectedVoiceId === voice.id}
                isPlaying={playingVoiceId === voice.id && isPlaying}
                onSelect={handleVoiceSelect}
                onPlay={handlePlaySample}
              />
            ))}
          </View>
        </View>

        {/* Error Display */}
        {error && (
          <View style={styles.errorContainer}>
            <NotificationCard
              variant="error"
              label="Settings Error"
              message={error}
            />
          </View>
        )}

        {/* Continue Button */}
        <View style={styles.buttonContainer}>
          <TextButton
            buttonText={isLoading ? "Saving..." : "Continue"}
            onPress={handleContinue}
            disabled={!selectedVoiceId || isLoading}
            width={undefined}
          />
        </View>
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    alignItems: "center",
    marginBottom: layout.space.xl
  },
  section: {
    marginBottom: layout.space.xl,
    zIndex: 1 // Ensure dropdown appears above other elements
  },
  sectionTitle: {
    marginBottom: layout.space.md
  },
  dropdownContainer: {
    zIndex: 2 // Ensure dropdown appears above other elements
  },
  voiceList: {
    // marginBottom: layout.space.xl // Removed to avoid double margin with section
  },
  errorContainer: {
    marginBottom: layout.space.lg
  },
  buttonContainer: {
    alignItems: "center",
    paddingBottom: layout.space.xl
  }
});
